import { Palette } from 'lucide-react';
import { EntityForm } from '@components/integrated/entity-form';
import type { EntityFormConfig } from '@components/integrated/entity-form';
import { useCEAuthStore } from '../auth/store';
import type { CELoginResponse } from '../auth/types';
import { Card, VStack, HStack } from '@components/base/layout';
import { Text, Icon } from '@components/base/display';

const TOKEN_KEY = 'ce_auth_token';

const loginFormConfig: EntityFormConfig = {
  id: 'component-explorer-login',
  endpoint: {
    erpEndpoint: '/api/component-explorer/auth/login',
    method: 'POST',
  },
  fields: {
    username: {
      type: 'text',
      label: 'Username',
      placeholder: 'Enter your username',
      order: 1,
      validation: { required: true },
    },
    password: {
      type: 'password',
      label: 'Password',
      placeholder: 'Enter your password',
      order: 2,
      validation: { required: true },
    },
  },
  actions: {
    submit: {
      label: 'Sign in',
      variant: 'primary',
      className: 'w-full',
    },
    cancel: { visible: false },
    reset: { visible: false },
  },
  workflow: {
    onError: {
      mapFieldErrors: true,
    },
  },
  layout: {
    columns: 1,
    gap: 'md',
  },
};

export function LoginPage() {
  const handleSuccess = async (response: unknown) => {
    const loginResponse = response as CELoginResponse;
    const { token, user } = loginResponse.data;
    localStorage.setItem(TOKEN_KEY, token);
    useCEAuthStore.setState({
      token,
      user,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
    // Login response doesn't include tenants — fetch them via /auth/me
    await useCEAuthStore.getState().fetchUser();
  };

  return (
    <div className="min-h-screen bg-secondary-50 flex items-center justify-center p-4">
      <Card variant="elevated" padding="lg" className="w-full max-w-md">
        <Card.Content>
          <VStack gap={6}>
            <VStack gap={2} align="center">
              <HStack gap={2} align="center">
                <Icon icon={Palette} size="lg" color="primary" />
                <Text as="h1" size="xl" weight="bold" color="primary">
                  Component Explorer
                </Text>
              </HStack>
              <Text size="sm" color="muted">
                Sign in to access the component library
              </Text>
            </VStack>

            <EntityForm config={loginFormConfig} onSuccess={handleSuccess}>
              <EntityForm.Alert />
              <EntityForm.Fields />
              <EntityForm.Actions />
            </EntityForm>
          </VStack>
        </Card.Content>
      </Card>
    </div>
  );
}
